import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard, Store, BarChart3,
  Settings, Users, LogOut, ChevronLeft, Menu, X, Sparkles, Lightbulb, MessageSquarePlus, Package, Wrench, UserCircle, IdCard, CalendarDays, ShoppingCart, Presentation, GraduationCap
} from 'lucide-react';
import { useChangelogUnread } from '../lib/useChangelogUnread';
import IdeaDialog from './IdeaDialog';
import IOSInstallBanner from './IOSInstallBanner';
import AndroidInstallBanner from './AndroidInstallBanner';
import NotificationsBell from './NotificationsBell';
import PersonalWorkspaceButton from './PersonalWorkspaceButton';

// Sidebar nav — refactor 2026-05-08 (idea_d61fb5573eba). Due gruppi:
//  • Operativo (quotidiano): Panoramica · Dashboard Area · Negozi · Utenti
//  • Sistema: Backlog · Changelog · Impostazioni
// Import Dati / Stato Dati / Mappatura Negozi / Registro Audit sono stati
// promossi a **tab interni** di Impostazioni (route legacy mantenute per
// non rompere bookmark/email vecchie — un redirect lato App.js le rimanda
// alla rispettiva tab `/settings?tab=imports|data-health|mappings|audit`).
// HR Manager ha accesso ESCLUSIVO alle pagine Staff/Profilo/Backlog/Changelog/Utenti.
// Le altre voci (Panoramica, Dashboard Area, Negozi, Magazzino, Assistenza,
// Impostazioni) sono nascoste per coerenza con la separation-of-duties HR.
// Per gli utenti "Staff" (`store_user`) la sidebar è volutamente minimale:
// solo 4 voci (Il mio profilo, Ferie, Il mio kit, Assistenza) + le voci
// pubbliche (Backlog, Changelog). Le dashboard operative restano invisibili
// per non confondere chi non ha accesso ai KPI di gestione.
const NAV_ITEMS = [
  // Voce dedicata Staff — sempre in cima per loro
  { path: '/my-profile', label: 'Il mio profilo', icon: UserCircle, roles: ['store_user'] },
  { path: '/', label: 'Panoramica', icon: LayoutDashboard, roles: ['operations_manager', 'area_manager', 'store_manager'] },
  { path: '/area-dashboard', label: 'Dashboard Area', icon: BarChart3, roles: ['operations_manager', 'area_manager', 'store_manager'] },
  { path: '/stores', label: 'Negozi', icon: Store, roles: ['operations_manager', 'area_manager', 'store_manager'] },
  { path: '/inventory', label: 'Magazzino', icon: Package, roles: ['operations_manager', 'area_manager', 'store_manager'] },
  { path: '/orders', label: 'Ordini', icon: ShoppingCart, roles: ['operations_manager', 'area_manager', 'store_manager'] },
  { path: '/presentations', label: 'Presentazioni', icon: Presentation, roles: ['operations_manager', 'area_manager'] },
  { path: '/staff', label: 'Staff', icon: IdCard, roles: ['operations_manager', 'hr_manager', 'area_manager', 'store_manager'] },
  { path: '/training', label: 'Formazione', icon: GraduationCap, roles: ['operations_manager', 'hr_manager', 'area_manager', 'store_manager', 'store_user'] },
  { path: '/leave', label: 'Ferie', icon: CalendarDays, roles: ['operations_manager', 'hr_manager', 'area_manager', 'store_manager', 'store_user'] },
  { path: '/my-kit', label: 'Il mio kit', icon: Package, roles: ['store_user'] },
  // Per i ruoli non-Staff "Il mio profilo" rimane in fondo come voce "personale"
  { path: '/my-profile', label: 'Il mio profilo', icon: UserCircle, roles: ['operations_manager', 'hr_manager', 'area_manager', 'store_manager'] },
  { path: '/assistance', label: 'Assistenza', icon: Wrench, roles: ['operations_manager', 'area_manager', 'store_manager', 'store_user'] },
  { path: '/users', label: 'Utenti', icon: Users, roles: ['operations_manager', 'hr_manager', 'area_manager'] },
  { type: 'divider' },
  { path: '/roadmap', label: 'Backlog', icon: Lightbulb },
  { path: '/changelog', label: 'Changelog', icon: Sparkles },
  { path: '/settings', label: 'Impostazioni', icon: Settings, roles: ['operations_manager'] },
];

const ROLE_LABELS = {
  operations_manager: 'Operations Manager',
  hr_manager: 'HR Manager',
  area_manager: 'Area Manager',
  store_manager: 'Store Manager',
  store_user: 'Staff',
};

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // `collapsed` è stato per desktop (icon-only sidebar), `mobileOpen` per il
  // drawer su mobile. Due stati separati così non interferiscono quando si
  // passa da mobile a desktop resizing la finestra.
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  // Pallino "What's new" sull'item Changelog: rosso se ci sono release dopo
  // l'ultima visita registrata in localStorage. Si spegne quando l'utente
  // apre /changelog (vedi `markChangelogVisited` chiamata in ChangelogPage).
  const { hasUnread: changelogUnread } = useChangelogUnread();

  const suggestPrefill = `Pagina: ${location.pathname}${location.search || ''}`;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Chiude il drawer mobile ad ogni cambio rotta.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  /** Titolo da mostrare nella topbar mobile. Match in ordine di specificità:
   * la route più lunga che prefissa il path corrente vince — così
   * `/stores/:id` continua a chiamarsi "Negozio" e non "Negozi". */
  const mobileTitle = (() => {
    const path = location.pathname;
    if (path.startsWith('/stores/') && path !== '/stores') return 'Negozio';
    const NAV_TITLES = {
      '/': 'Panoramica',
      '/area-dashboard': 'Dashboard Area',
      '/stores': 'Negozi',
      '/orders': 'Ordini',
      '/training': 'Formazione',
      '/presentations': 'Presentazioni',
      '/imports': 'Impostazioni · Import Dati',
      '/data-health': 'Impostazioni · Diagnostica',
      '/settings': 'Impostazioni',
      '/users': 'Utenti',
      '/store-mappings': 'Impostazioni · Mappatura Negozi',
      '/audit-log': 'Impostazioni · Registro Audit',
      '/roadmap': 'Backlog',
      '/changelog': 'Changelog',
    };
    return NAV_TITLES[path] || '';
  })();

  // Blocca lo scroll del body quando il drawer è aperto (evita "doppio scroll").
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [mobileOpen]);

  return (
    <div className="min-h-screen md:flex bg-slate-50" data-testid="app-layout">
      {/* Top bar mobile — visibile solo < md. Hamburger + logo + badge utente.
          `app-safe-top` aggiunge padding pari all'`safe-area-inset-top` di iOS
          così l'header non finisce sotto la Dynamic Island/status bar quando
          l'app gira in standalone (PWA installata). */}
      <header
        className="md:hidden sticky top-0 z-40 bg-white border-b border-slate-200 app-safe-top"
        data-testid="mobile-topbar"
      >
        <div className="h-14 flex items-center justify-between px-4 gap-3">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 hover:bg-slate-100 transition-colors flex-shrink-0"
            data-testid="mobile-menu-open-btn"
            aria-label="Apri menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div
            className="flex-1 min-w-0 flex items-center justify-center gap-2"
            data-testid="mobile-topbar-title"
          >
            {mobileTitle ? (
              // Titolo della pagina centrato — pattern iOS nativo. Niente
              // logo nell'app: lo mostra solo il drawer aperto.
              <span className="font-heading font-semibold text-base tracking-tight truncate">
                {mobileTitle}
              </span>
            ) : (
              <Link to="/" className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 bg-black flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-heading font-bold text-xs">S</span>
                </div>
                <span className="font-heading font-bold text-base tracking-tight truncate">StoreOps</span>
              </Link>
            )}
          </div>
          <div className="flex items-center gap-1">
            <PersonalWorkspaceButton />
            <NotificationsBell />
            <button
              onClick={() => setSuggestOpen(true)}
              className="p-2 -mr-2 hover:bg-slate-100 transition-colors flex-shrink-0"
              data-testid="mobile-suggest-btn"
              aria-label="Suggerisci miglioramento"
            >
              <MessageSquarePlus className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>
      </header>

      {/* Overlay drawer — mobile only */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 bg-black/40"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-drawer-overlay"
          aria-hidden="true"
        />
      )}

      {/* Sidebar — desktop stays static, mobile is a slide-in drawer.
          Quando aperto come drawer su iOS standalone, `app-safe-top` evita
          che il logo finisca sotto la status bar. */}
      <aside
        className={`bg-white border-r border-slate-200 flex flex-col transition-all duration-200 ${
          collapsed ? 'md:w-16' : 'md:w-60'
        } ${
          mobileOpen
            ? 'fixed inset-y-0 left-0 w-72 z-50 app-safe-top md:static md:z-auto md:pt-0'
            : 'hidden md:flex'
        }`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center px-4 border-b border-slate-200 justify-between">
          {(!collapsed || mobileOpen) && (
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-black flex items-center justify-center flex-shrink-0">
                <span className="text-white font-heading font-bold text-sm">S</span>
              </div>
              <span className="font-heading font-bold text-base tracking-tight">StoreOps</span>
            </Link>
          )}
          {/* Toggle collapse (desktop) OR close drawer (mobile) */}
          <button
            onClick={() => {
              if (mobileOpen) setMobileOpen(false);
              else setCollapsed(!collapsed);
            }}
            className="p-1.5 hover:bg-slate-100 transition-colors"
            data-testid="sidebar-toggle"
            aria-label={mobileOpen ? 'Chiudi menu' : (collapsed ? 'Espandi menu' : 'Comprimi menu')}
          >
            {mobileOpen ? (
              <X className="w-4 h-4" />
            ) : collapsed ? (
              <Menu className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map((item, i) => {
            if (item.type === 'divider') {
              return <div key={`divider-${i}`} className="my-2 border-t border-slate-100" />;
            }
            if (item.roles && !item.roles.includes(user?.role)) {
              return null;
            }
            const Icon = item.icon;
            const active = location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path));
            const showLabel = mobileOpen || !collapsed;
            const showUnreadDot = item.path === '/changelog' && changelogUnread;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`relative flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
                data-testid={`nav-${item.path.replace(/\//g, '') || 'overview'}`}
                title={!showLabel ? item.label : undefined}
              >
                <span className="relative flex-shrink-0">
                  <Icon className="w-4 h-4" />
                  {showUnreadDot && (
                    <span
                      className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white"
                      data-testid="changelog-unread-dot"
                      aria-label="Nuove release nel changelog"
                    />
                  )}
                </span>
                {showLabel && (
                  <span className="flex items-center gap-2">
                    {item.label}
                    {showUnreadDot && (
                      <span
                        className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 bg-red-500 text-white rounded-sm"
                        data-testid="changelog-unread-badge"
                      >
                        Novità
                      </span>
                    )}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        {/* Rimosso 2026-05-08 (idea_d61fb5573eba): il "periodo selezionato"
            in fondo alla sidebar era rumore visivo. Il filtro periodo
            esiste solo nell'header delle pagine che lo usano. */}
        <div className="border-t border-slate-200 p-3">
          <button
            onClick={() => setSuggestOpen(true)}
            className={`w-full flex items-center gap-2 px-2 py-2 mb-2 text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors ${
              collapsed && !mobileOpen ? 'justify-center' : ''
            }`}
            data-testid="suggest-improvement-btn"
            title={collapsed && !mobileOpen ? 'Suggerisci miglioramento' : undefined}
          >
            <MessageSquarePlus className="w-4 h-4 flex-shrink-0" />
            {(mobileOpen || !collapsed) && <span>Suggerisci miglioramento</span>}
          </button>
          {(mobileOpen || !collapsed) && user && (
            <div className="mb-2">
              <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
              <p className="text-xs text-slate-500 truncate">{ROLE_LABELS[user.role] || user.role}</p>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-slate-500 hover:text-slate-900 rounded-none"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            {(mobileOpen || !collapsed) && 'Esci'}
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto min-w-0">
        {/* Desktop topbar — solo la campanella notifiche, allineata a destra.
            Su mobile la campanella è già nell'header sticky in cima. */}
        <div className="hidden md:flex sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-100 px-4 py-2 justify-end items-center gap-1" data-testid="desktop-topbar">
          <PersonalWorkspaceButton />
          <NotificationsBell />
        </div>
        <Outlet />
      </main>

      <IdeaDialog
        open={suggestOpen}
        idea={null}
        prefillDescription={suggestPrefill}
        canEditWorkflow={user?.role === 'operations_manager'}
        onClose={() => setSuggestOpen(false)}
        onSaved={() => setSuggestOpen(false)}
      />
      <IOSInstallBanner />
      <AndroidInstallBanner />
    </div>
  );
}
